import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, Activity, AlertTriangle, Package, ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function InfirmerieDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/infirmary/dashboard");
      setData(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="infirmerie-dashboard">
      <div>
        <div className="chip mb-2"><Heart className="h-3 w-3" /><span>INFIRMERIE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Tableau de bord infirmerie</h1>
        <p className="text-slate-500 mt-1">Suivi médical des élèves, passages et stock pharmacie.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI label="Passages aujourd'hui" value={data?.visits_today || 0} icon={Activity} color="text-[#1E3A8A]" bg="bg-blue-50" />
        <KPI label="Passages du mois" value={data?.visits_month || 0} icon={TrendingUp} color="text-[#166534]" bg="bg-green-50" />
        <KPI label="Articles stock bas" value={data?.low_stock_count || 0} icon={Package} color="text-[#EA580C]" bg="bg-orange-50" />
        <KPI label="Médicaments expirant" value={data?.expiring_count || 0} icon={AlertTriangle} color="text-red-600" bg="bg-red-50" />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/dashboard/infirmerie/passages" title="Registre des passages" desc="Enregistrer un soin, consulter l'historique." color="bg-pink-600" />
        <QuickLink to="/dashboard/infirmerie/fiches" title="Fiches médicales" desc="Allergies, groupe sanguin, contacts urgence." color="bg-[#1E3A8A]" />
        <QuickLink to="/dashboard/infirmerie/pharmacie" title="Pharmacie & Stock" desc="Médicaments, pansements, alertes péremption." color="bg-[#166534]" />
      </div>

      {/* Top motifs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Top 5 motifs du mois</h2>
          {(data?.top_motifs || []).length === 0 ? (
            <div className="text-sm text-slate-400">Aucun passage enregistré ce mois.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_motifs.map((m, i) => (
                <li key={i} className="flex items-center justify-between text-sm" data-testid={`motif-${i}`}>
                  <span className="capitalize">{m.motif}</span>
                  <span className="font-bold text-[#1E3A8A] tabular-nums">{m.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-lg text-[#EA580C] mb-3">⚠️ Alertes pharmacie</h2>
          {(data?.low_stock || []).length === 0 && (data?.expiring || []).length === 0 ? (
            <div className="text-sm text-slate-400">Aucune alerte. Bon stock.</div>
          ) : (
            <div className="space-y-2 text-sm">
              {data.low_stock.map(i => (
                <div key={i.id} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                  <span>{i.name}</span>
                  <span className="text-xs font-bold text-[#EA580C]">Stock bas : {i.qty} {i.unit}</span>
                </div>
              ))}
              {data.expiring.map(i => (
                <div key={i.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                  <span>{i.name}</span>
                  <span className="text-xs font-bold text-red-600">{i.days_to_expiry > 0 ? `Expire dans ${i.days_to_expiry}j` : `Expiré (${-i.days_to_expiry}j)`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} rounded-2xl border border-white p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`text-3xl font-extrabold ${color} tabular-nums`}>{value}</div>
    </div>
  );
}

function QuickLink({ to, title, desc, color }) {
  return (
    <Link to={to} className="group block bg-white border border-slate-200 hover:border-[#1E3A8A] rounded-2xl p-5 transition-all hover:shadow-lg">
      <div className={`w-10 h-10 ${color} rounded-xl mb-3 group-hover:scale-110 transition-transform flex items-center justify-center`}>
        <ArrowRight className="h-5 w-5 text-white" />
      </div>
      <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">{title}</div>
      <div className="text-sm text-slate-500">{desc}</div>
    </Link>
  );
}
